import React, { useEffect, useState } from 'react';
import { MapPin, Edit, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const AdminLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLocation, setEditingLocation] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLocations = () => {
    axios.get(`${API}/locations`)
      .then(res => setLocations(res.data))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    // Parse JSON fields with error handling
    try {
      data.opening_hours = JSON.parse(data.opening_hours || '{}');
    } catch (err) {
      toast.error('Ungültiges JSON-Format bei Öffnungszeiten');
      return;
    }
    
    data.features = data.features.split('\n').filter(f => f.trim());
    
    try {
      data.faqs = JSON.parse(data.faqs || '[]');
    } catch (err) {
      toast.error('Ungültiges JSON-Format bei FAQs');
      return;
    }

    try {
      if (editingLocation) {
        await axios.put(`${API}/locations/${editingLocation.slug}`, data, { headers });
        toast.success('Standort aktualisiert!');
      } else {
        await axios.post(`${API}/locations`, data, { headers });
        toast.success('Standort erstellt!');
      }
      setIsDialogOpen(false);
      setEditingLocation(null);
      fetchLocations();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Möchtest du diesen Standort wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/locations/${slug}`, { headers });
      toast.success('Standort gelöscht!');
      fetchLocations();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  return (
    <div data-testid="admin-locations">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Standorte</h1>
          <p className="text-slate-600">Verwalte die Standort-Seiten deiner Fahrschule.</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingLocation(null)} data-testid="add-location-btn">
              <Plus className="w-4 h-4 mr-2" /> Neuer Standort
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingLocation ? 'Standort bearbeiten' : 'Neuer Standort'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" defaultValue={editingLocation?.name} required />
                </div>
                <div>
                  <Label htmlFor="slug">Slug (URL)</Label>
                  <Input id="slug" name="slug" defaultValue={editingLocation?.slug} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">Stadt</Label>
                  <Input id="city" name="city" defaultValue={editingLocation?.city} required />
                </div>
                <div>
                  <Label htmlFor="address">Adresse</Label>
                  <Input id="address" name="address" defaultValue={editingLocation?.address} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Telefon</Label>
                  <Input id="phone" name="phone" defaultValue={editingLocation?.phone} />
                </div>
                <div>
                  <Label htmlFor="email">E-Mail</Label>
                  <Input id="email" name="email" type="email" defaultValue={editingLocation?.email} />
                </div>
              </div>
              <div>
                <Label htmlFor="hero_title">Hero Titel (H1)</Label>
                <Input id="hero_title" name="hero_title" defaultValue={editingLocation?.hero_title} />
              </div>
              <div>
                <Label htmlFor="hero_subtitle">Hero Untertitel</Label>
                <Input id="hero_subtitle" name="hero_subtitle" defaultValue={editingLocation?.hero_subtitle} />
              </div>
              <div>
                <Label htmlFor="content">Inhalt (Markdown)</Label>
                <Textarea id="content" name="content" rows={8} defaultValue={editingLocation?.content} />
              </div>
              <div>
                <Label htmlFor="features">Vorteile (eine pro Zeile)</Label>
                <Textarea id="features" name="features" rows={4} defaultValue={editingLocation?.features?.join('\n')} />
              </div>
              <div>
                <Label htmlFor="opening_hours">Öffnungszeiten (JSON)</Label>
                <Textarea 
                  id="opening_hours" 
                  name="opening_hours" 
                  rows={3} 
                  defaultValue={JSON.stringify(editingLocation?.opening_hours || {"mo_fr": "09:00 - 18:00", "sa": "10:00 - 14:00"}, null, 2)} 
                  placeholder='{"mo_fr": "09:00 - 18:00", "sa": "10:00 - 14:00"}'
                />
              </div>
              <div>
                <Label htmlFor="faqs">FAQs (JSON Array)</Label>
                <Textarea 
                  id="faqs" 
                  name="faqs" 
                  rows={4} 
                  defaultValue={JSON.stringify(editingLocation?.faqs || [], null, 2)} 
                  placeholder='[{"question": "Frage?", "answer": "Antwort"}]'
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="meta_title">Meta Title</Label>
                  <Input id="meta_title" name="meta_title" defaultValue={editingLocation?.meta_title} />
                </div>
                <div>
                  <Label htmlFor="meta_description">Meta Description</Label>
                  <Input id="meta_description" name="meta_description" defaultValue={editingLocation?.meta_description} />
                </div>
              </div>
              <div>
                <Label htmlFor="map_embed">Google Maps Suchbegriff</Label>
                <Input id="map_embed" name="map_embed" defaultValue={editingLocation?.map_embed} />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit">Speichern</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {locations.map((location) => (
          <Card key={location.slug} data-testid={`location-item-${location.slug}`}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-lg font-semibold">{location.name}</CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => { setEditingLocation(location); setIsDialogOpen(true); }}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => handleDelete(location.slug)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-600">{location.address}</p>
                  <p className="text-sm text-slate-500 mt-1">{location.phone} • {location.email}</p>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <a 
                  href={`/standorte/${location.slug}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Seite ansehen →
                </a>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminLocations;
